/**
 * @fileoverview A seed-packet scan's packets as rows the gardener settles: one model for the review sheet
 * (PacketReview, Today's door 1) and the "Check and save" packet card (PacketPile), so both file a packet the same
 * way (D78 rev 2 §3: "one PacketRow component for the card and the sheet").
 *
 * Each row starts from the scan: what is printed, the plant and variety the reader placed it under (its Type "Not
 * sure" when the packet named only the crop, B52), a new variety's name, its box in the photo, and ticked when it is
 * placed. The gardener can untick it, change its plant, type or variety, give it a bed of its own (D78 rev 2 §2) or
 * remove it, in the row's change dialog. A new variety is named by the server (audit A35): each row sends its name as
 * typed and the kind to join (joinKind).
 *
 * The rows' type and crop names, their varieties and where Variety starts are the server's: the varieties answer of each
 * row's type (W24 Job B, usePickerLookup), read back as they are; nothing is matched or started here.
 *
 * The Seed vault card (PacketKeep, B137) uses the same rows in a keep mode: every row is tickable (one Grow could not place
 * goes in as printed), starts ticked unless the server says its seed is already in the Seed vault (inVault), and keepItem
 * builds what POST /packets/keep is sent (no bed, no stage).
 *
 * Where: PacketReview, PacketPile, PacketKeep.
 * API: none (rowItem builds what POST /packets/confirm is sent; keepItem what POST /packets/keep is sent).
 *
 * @module packetRows
 */

import { kindToJoin, type KeepItem, type PacketItem, type PacketScan, type ScannedPacket } from '@/shared/api/packets';
import { EMPTY_CHOICE, type CropChoice, type PickerLookup } from '@/shared/components/plant-fields/usePlantPicker';

/** A row's bed of its own: one of theirs, or a new one by name (empty while it is being typed). */
export type OwnBed = { kind: 'bed'; id: number } | { kind: 'new'; name: string };

/** One packet as the gardener is settling it. */
export interface PacketRowState {
  /** Its place in scan.packets. */
  key: number;
  /** Taken out ("Remove this packet", or unticked on an older tray card): not drawn, no Undo (B45). */
  removed: boolean;
  /** Ticked: it is filed. */
  ticked: boolean;
  /** As printed. */
  kind: string;
  /** The variety as printed; null when the packet names none. */
  variety: string | null;
  /** The plant and variety chosen (or read) for it; EMPTY_CHOICE until its type's answer is in, or for one Grow could not place. */
  choice: CropChoice;
  /** A variety nobody has yet, made as theirs on confirming; null for none. */
  newVariety: string | null;
  /** Where its packet is in the photo; null for none found. */
  box: [number, number, number, number] | null;
  /** The variety the scan matched (Grow's or theirs), by id and name: named even before the list has it. */
  known: { id: number; name: string } | null;
  /** The kind the server joins to its new variety under a broad flower parent (null for a name already whole). */
  joinKind: string | null;
  /** The name the server gave the placed packet ("Italian Aromatic Sage"), shown until the gardener changes the row. */
  placedName: string | null;
  /** Changed in its dialog: named from the fields from then on. */
  edited: boolean;
  /** Its own bed; null for the one the others go to. */
  bed: OwnBed | null;
  /** The server says its seed is already in her Seed vault (shown "Already in your Seed vault", and starts unticked). */
  inVault: boolean;
  /** For a packet Grow could not place: its own suggestion, kept so the vault's "Grow thinks this is" still works. */
  suggestion: ScannedPacket['suggestion'];
}

/**
 * A scan's packets as rows: the Seed vault card's (keep, the default) are ticked unless the server says the seed is already
 * in her vault, a packet Grow could not place too; today's card and the review sheet's (keep false) tick a packet when the
 * reader placed it.
 *
 * @param scan - The packets as read.
 * @param removedKeys - Packets (by their place) to start out taken out.
 * @param keep - The Seed vault card's rows (B137), not a bed's.
 * @returns The rows, in the photo's order.
 */
export function rowsOf(scan: PacketScan, removedKeys: readonly number[] = [], keep = true): PacketRowState[] {
  return scan.packets.map((packet, key) => ({
    key,
    removed: removedKeys.includes(key),
    ticked: keep ? !packet.inVault : packet.plantId != null,
    kind: packet.kind ?? '',
    variety: packet.variety ?? null,
    choice: EMPTY_CHOICE,
    // One Grow could not place keeps its printed variety, for whichever plant the gardener picks.
    newVariety: packet.plantId == null ? packet.variety ?? null : packet.newVariety ?? null,
    box: packet.box ?? null,
    known: packet.varietyId != null && packet.varietyName ? { id: packet.varietyId, name: packet.varietyName } : null,
    joinKind: kindToJoin(packet),
    placedName: packet.placedName ?? null,
    edited: false,
    bed: null,
    inVault: Boolean(packet.inVault),
    suggestion: packet.suggestion ?? null,
  }));
}

/**
 * Reads a saved type and variety back as a choice, its crop from the type's answer. A Type-not-sure one (B52) is the
 * crop alone with the flag, no type and no variety.
 *
 * @param lookup - The rows' types' answers.
 * @returns choiceOf(plantId, varietyId, typeUnsure): the choice.
 */
const choiceReader = (lookup: PickerLookup) => (plantId: number, varietyId: number | null, typeUnsure?: boolean | null): CropChoice => {
  const cropId = lookup.cropOf(plantId);
  if (typeUnsure && cropId !== null) return { cropId, plantId: null, varietyId: null, typeUnsure: true };
  return { cropId, plantId, varietyId };
};

/**
 * The rows' plant and variety set once their types' answers are in: each placed packet as the reader placed it, on the
 * variety the server starts its type on where the reader named none, its Type "Not sure" flag kept (B52).
 *
 * @param rows - The rows.
 * @param scan - The packets as read.
 * @param lookup - The rows' types' answers.
 * @returns The rows with their choices.
 */
export function seededRows(rows: PacketRowState[], scan: PacketScan, lookup: PickerLookup): PacketRowState[] {
  const choiceOf = choiceReader(lookup);
  return rows.map((row) => {
    const packet = scan.packets[row.key];
    if (packet?.plantId == null) return row;
    const varietyId = packet.varietyId ?? lookup.varietiesOf(packet.plantId).start;
    return { ...row, choice: choiceOf(packet.plantId, varietyId, packet.typeUnsure) };
  });
}

/**
 * Whether a row has its answer: a plant, or a crop on Type "Not sure" (B52).
 *
 * @param row - The row.
 */
export const isPlaced = (row: Pick<PacketRowState, 'choice'>): boolean => row.choice.plantId !== null || Boolean(row.choice.typeUnsure);

/**
 * The variety a row stands for: a named one in the list, or the one the scan matched when the list does not have it
 * yet; null for "Not sure" or a new one.
 *
 * @param index - The rows' types' answers (usePickerLookup).
 * @param row - The row.
 */
export function chosenVariety(index: PickerLookup, row: PacketRowState): { id: number; name: string } | null {
  if (row.choice.varietyId === null) return null;
  const named = index.varietiesOf(row.choice.plantId).named.find((v) => v.id === row.choice.varietyId);
  if (named) return named;
  return row.known && row.known.id === row.choice.varietyId ? row.known : null;
}

/**
 * Words lowered and run together, for telling whether two say the same.
 *
 * @param text - Anything.
 */
export const plain = (text: string | null | undefined): string => String(text ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** What a row says. */
export interface RowWords {
  /** Its name in one line: the server's placed name, or plant · variety, or as printed. */
  name: string;
  /** The plant's name alone, or null while it has none. */
  plant: string | null;
  /** Its variety's name, or null. */
  variety: string | null;
  /** A variety nobody has yet. */
  isNew: boolean;
  /** What the packet says, "Tomato · Cherokee Purple". */
  printed: string;
}

/**
 * What a row says: its plant (a crop alone on Type "Not sure", as the server will name it), its variety and the
 * "New variety" mark, and what is printed.
 *
 * @param index - The rows' types' answers (usePickerLookup).
 * @param row - The row.
 * @returns RowWords.
 */
export function rowWords(index: PickerLookup, row: PacketRowState): RowWords {
  const plant = row.choice.typeUnsure ? index.cropName(row.choice.cropId)
    : row.choice.plantId !== null ? index.plantName(row.choice.plantId) : null;
  const named = chosenVariety(index, row);
  const variety = named?.name ?? row.newVariety ?? null;
  const isNew = !named && Boolean(row.newVariety);
  const printed = [row.kind, row.variety].filter(Boolean).join(' · ');
  const name = !row.edited && row.placedName && isPlaced(row) ? row.placedName : [plant ?? row.kind, variety].filter(Boolean).join(' · ');
  return { name, plant, variety, isNew, printed };
}

/**
 * What POST /packets/confirm is sent for a row: Type "Not sure" (B52) as the crop and the flag (the server files the
 * crop's usual type; the app never picks it); else its plant and a named variety, or a new one as typed with the kind
 * to join; and its own bed, when it has one (D78 rev 2 §2). A row given its type before that type's answer was in (a
 * suggestion found by a free look-up) goes on the variety the server starts the type on.
 *
 * @param index - The rows' types' answers (usePickerLookup).
 * @param row - A placed row.
 * @param shared - The bed the others go to, as the request names it: a row whose own bed is that one sends none.
 * @returns The item.
 */
export function rowItem(index: PickerLookup, row: PacketRowState, shared?: { locationId: number } | { newBedName: string }): PacketItem {
  const box = row.box ?? null;
  const own: Pick<PacketItem, 'locationId' | 'newBedName'> = row.bed?.kind === 'bed'
    ? (shared && 'locationId' in shared && shared.locationId === row.bed.id ? {} : { locationId: row.bed.id })
    : row.bed?.kind === 'new' ? { newBedName: row.bed.name.trim() } : {};
  if (row.choice.typeUnsure && row.choice.cropId !== null) return { cropId: row.choice.cropId, typeUnsure: true, box, ...own };
  const named = chosenVariety(index, row);
  const plantId = Number(row.choice.plantId);
  // A new variety as typed, with the kind the server joins to it under a broad flower parent ("Odd One Zinnia").
  const newVariety = row.newVariety?.trim() || null;
  if (named) return { plantId, varietyId: named.id, box, ...own };
  if (newVariety) return { plantId, newVariety, kind: row.joinKind, box, ...own };
  return { plantId, varietyId: row.choice.varietyId ?? index.varietiesOf(plantId).start, box, ...own };
}

/**
 * What POST /packets/keep is sent for a ticked row: its name and printed words, and its plant as rowItem has it when it
 * has one (Type "Not sure" as the crop and the flag, a named or new variety); a packet Grow could not place goes with no
 * plant and its own suggestion. A kept seed has no bed and no stage, so none is sent.
 *
 * @param index - The rows' types' answers (usePickerLookup).
 * @param row - A ticked row.
 * @returns The item.
 */
export function keepItem(index: PickerLookup, row: PacketRowState): KeepItem {
  const head = { name: rowWords(index, row).name, printed: { kind: row.kind, variety: row.variety }, box: row.box ?? null };
  if (!isPlaced(row)) return { ...head, plantId: null, suggestion: row.suggestion };
  // A kept seed has no bed: the row's own bed is left out, so rowItem names none.
  return { ...head, ...rowItem(index, { ...row, bed: null }) };
}
